import React, { useState } from 'react';
import { Edit3, Save, X } from 'lucide-react';

/**
 * BooleanField - Componente para campos booleanos editables con dos vistas.
 * * @param {string} field - Identificador del campo.
 * @param {string} label - Etiqueta del campo.
 * @param {boolean} value - Valor actual (true/false/null/undefined).
 * @param {boolean} isAdmin - Si mostrar controles de edición.
 * @param {Function} onSave - Callback al guardar (field, value).
 * @param {boolean} required - Si el campo es obligatorio.
 * @param {boolean} disabled - Si está deshabilitado.
 * @param {string} className - Clases CSS adicionales.
 * @param {Object} labels - Etiquetas personalizadas { true: 'Sí', false: 'No' }.
 * @param {'radio' | 'toggle'} displayAs - Cómo mostrar el control de edición.
 */
const BooleanField = ({
  field,
  label,
  value,
  isAdmin = false,
  onSave,
  required = false,
  disabled = false,
  className = '',
  labels = {
    true: 'Sí',
    false: 'No',
  },
  displayAs = 'toggle' // Nueva prop para seleccionar la vista
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(null);
  const [error, setError] = useState('');

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    // Para el toggle, si el valor es null/undefined, lo iniciamos en `false`.
    const initialEditValue = displayAs === 'toggle' ? (value ?? false) : value;
    setEditValue(initialEditValue);
    setError('');
  };

  const handleSave = async () => {
    if (required && (editValue === null || editValue === undefined)) {
      setError('Este campo es obligatorio');
      return;
    }

    try {
      if (onSave) {
        await onSave(field, editValue);
      }
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Error al guardar');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(null);
    setError('');
  };

  const handleOptionChange = (newValue) => {
    setEditValue(newValue);
    setError('');
  };

  const getDisplayValue = () => {
    if (value === true) return labels.true;
    if (value === false) return labels.false;
    // Si la vista es toggle, mostramos "No" en lugar de un texto para nulos.
    if (displayAs === 'toggle') return labels.false;
    return 'No especificado';
  };

  const getDisplayClass = () => {
    if ((value === null || value === undefined) && displayAs !== 'toggle') {
      return 'text-gray-400 italic';
    }
    return 'font-medium text-gray-800';
  };

  return (
    <div className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg ${className}`}>
      <div className="flex-1 min-w-0">
        <label className="text-sm text-gray-600 block mb-1">
          {label}
          {required && displayAs !== 'toggle' && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {isEditing ? (
          <div>
            {/* --- VISTA DE TOGGLE --- */}
            {displayAs === 'toggle' ? (
              <label htmlFor={`${field}_toggle`} className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id={`${field}_toggle`}
                  className="sr-only peer"
                  checked={editValue === true}
                  onChange={() => handleOptionChange(!editValue)}
                  disabled={disabled}
                />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:bg-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 select-none">
                  {editValue ? labels.true : labels.false}
                </span>
              </label>
            ) : (
              /* --- VISTA DE RADIO (Original) --- */
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`${field}_boolean`}
                    checked={editValue === true}
                    onChange={() => handleOptionChange(true)}
                    className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    disabled={disabled}
                  />
                  <span className="text-sm text-gray-700">{labels.true}</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`${field}_boolean`}
                    checked={editValue === false}
                    onChange={() => handleOptionChange(false)}
                    className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    disabled={disabled}
                  />
                  <span className="text-sm text-gray-700">{labels.false}</span>
                </label>
              </div>
            )}
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          </div>
        ) : (
          <span className={`break-words ${getDisplayClass()}`}>
            {getDisplayValue()}
          </span>
        )}
      </div>
      
      {isAdmin && (
        <div className="ml-4 flex gap-2 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={disabled}
                className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                title="Guardar"
              >
                <Save size={16} />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 text-red-600 hover:bg-red-100 rounded"
                title="Cancelar"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              disabled={disabled}
              className="p-1 text-blue-600 hover:bg-blue-100 rounded disabled:opacity-50"
              title="Editar"
            >
              <Edit3 size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BooleanField;